import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUp, ChevronDown, ArrowUpRight, Flame, TrendingDown, CloudRain } from 'lucide-react';
import { LeagueContext, LeagueContextData } from '../../components/layouts/LeagueLayout';
import Card from '../../components/common/Card';
import GradeStamp from '../../components/common/GradeStamp';
import { computeDraftGrade, LetterGrade } from '../../utils/gradeEngine';
import { analyticsApi, v1Api } from '../../services/api';
import { LeagueDraftAnalytics } from '../../types';

interface SuperlativeWinner {
  team_id: number;
  team_name: string;
  player_name?: string;
  value: number;
  display: string;
}
interface Superlative {
  award: string;
  description: string;
  winner: SuperlativeWinner;
}

const LeagueOverview: React.FC = () => {
  const context = useContext(LeagueContext) as LeagueContextData;
  const navigate = useNavigate();
  const { espnLeagueId } = useParams<{ espnLeagueId: string }>();
  const { standings, league } = context;

  const [sortField, setSortField] = useState<'rank' | 'team_name' | 'regular_season_rank' | 'regular_season_record' | 'points_for' | 'points_against' | 'differential'>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null);
  const [superlatives, setSuperlatives] = useState<Superlative[] | null>(null);
  const [draftAnalytics, setDraftAnalytics] = useState<LeagueDraftAnalytics | null>(null);

  // Lazy grade fetch — fires once the page is up, never blocks standings render
  useEffect(() => {
    if (!league?.id) return;
    v1Api.leagues
      .getLeagueDraftAnalytics(league.id)
      .then(setDraftAnalytics)
      .catch((err) => console.warn('Draft analytics load failed:', err));
  }, [league?.id]);

  const draftGradeByTeam = useMemo(() => {
    const map = new Map<number, LetterGrade>();
    draftAnalytics?.teams.forEach((t) =>
      map.set(t.team_id, computeDraftGrade(t.draft_performance.avg_value_score))
    );
    return map;
  }, [draftAnalytics]);

  // Fetch superlatives for the "Season at a glance" insight panel (data already computed by awards API)
  const fetchSuperlatives = useCallback(async () => {
    if (!espnLeagueId) return;
    try {
      const data = await analyticsApi.getSuperlatives(parseInt(espnLeagueId));
      setSuperlatives(data.superlatives || null);
    } catch (err) {
      console.warn('Superlatives load failed:', err);
    }
  }, [espnLeagueId]);

  useEffect(() => {
    fetchSuperlatives();
  }, [fetchSuperlatives]);

  const handleTeamClick = (teamId: number) => {
    navigate(`/leagues/espn/${espnLeagueId}/teams/${teamId}`);
  };

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'rank' || field === 'regular_season_rank' ? 'asc' : 'desc');
    }
  };

  const getSortedStandings = (standingsData: any[]) => {
    return [...standingsData].sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (sortField) {
        case 'rank':
          valueA = a.rank; valueB = b.rank; break;
        case 'team_name':
          valueA = a.team_name.toLowerCase(); valueB = b.team_name.toLowerCase(); break;
        case 'regular_season_rank':
          valueA = a.regular_season?.rank || 0; valueB = b.regular_season?.rank || 0; break;
        case 'regular_season_record':
          valueA = a.regular_season?.wins || 0; valueB = b.regular_season?.wins || 0; break;
        case 'points_for':
          valueA = a.regular_season?.points_for || 0; valueB = b.regular_season?.points_for || 0; break;
        case 'points_against':
          valueA = a.regular_season?.points_against || 0; valueB = b.regular_season?.points_against || 0; break;
        case 'differential':
          valueA = a.regular_season?.point_differential || 0; valueB = b.regular_season?.point_differential || 0; break;
        default:
          valueA = a.rank; valueB = b.rank;
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableHeader: React.FC<{ field: typeof sortField; children: React.ReactNode; className?: string }> = ({ field, children, className = "" }) => {
    const align = className.includes('text-right') ? 'justify-end' : className.includes('text-center') ? 'justify-center' : 'justify-start';
    return (
      <th
        className={`cursor-pointer hover:bg-background-tertiary/50 transition-colors ${className}`}
        onClick={() => handleSort(field)}
      >
        <div className={`flex items-center gap-1 ${align}`}>
          {children}
          {sortField === field && (
            sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
          )}
        </div>
      </th>
    );
  };

  // "Season at a glance" insights sourced from awards/superlatives API
  const findAward = (name: string) => superlatives?.find(s => s.award === name);
  const insights = [
    { icon: Flame, label: 'Steal of the Draft', award: findAward('Draft Boom of the Year') },
    { icon: TrendingDown, label: 'Worst Money Spent', award: findAward('Biggest Draft Bust') },
    { icon: CloudRain, label: 'Robbed in Broad Daylight', award: findAward('Unluckiest Loss') },
  ].filter(i => i.award);

  return (
    <>
      <style>{`
        .overview-layout {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        @media (min-width: 1024px) {
          .overview-layout {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 2rem;
            align-items: start;
          }
        }
      `}</style>

      <div className="overview-layout">
        {/* Left Column — Season at a glance insight panel */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-base font-semibold text-text-primary mb-4">The Receipts</h3>
            {insights.length > 0 ? (
              <div className="divide-y divide-fieldline">
                {insights.map(({ icon: Icon, label, award }) => (
                  <button
                    key={label}
                    onClick={() => navigate(`/leagues/espn/${espnLeagueId}/awards`)}
                    className="w-full flex items-start gap-3 py-3 text-left group first:pt-0 last:pb-0"
                  >
                    <Icon className="w-4 h-4 mt-0.5 text-text-secondary flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs uppercase tracking-wide text-text-secondary">{label}</p>
                      <p className="text-text-primary font-medium truncate">{award!.winner.team_name}</p>
                      <p className="font-mono text-sm text-text-secondary truncate">{award!.winner.display}</p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-text-secondary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-text-secondary text-sm">Loading insights…</p>
            )}
          </Card>
        </div>

        {/* Right Column — League Standings */}
        <div>
          {standings && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-text-primary mb-6">League Standings</h2>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-fieldline">
                      <SortableHeader field="rank" className="text-left py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-start">
                          <span>Final Rank</span>
                          <span className="text-xs text-text-tertiary font-normal">Playoff Result</span>
                        </div>
                      </SortableHeader>
                      <SortableHeader field="team_name" className="text-left py-3 px-4 text-text-secondary font-medium text-sm">
                        Team
                      </SortableHeader>
                      <SortableHeader field="regular_season_rank" className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-center">
                          <span>Reg. Rank</span>
                          <span className="text-xs text-text-tertiary font-normal">Weeks 1-14</span>
                        </div>
                      </SortableHeader>
                      <SortableHeader field="regular_season_record" className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-center">
                          <span>Reg. Season</span>
                          <span className="text-xs text-text-tertiary font-normal">W-L Record</span>
                        </div>
                      </SortableHeader>
                      <SortableHeader field="points_for" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-end">
                          <span>Points For</span>
                          <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                        </div>
                      </SortableHeader>
                      <SortableHeader field="points_against" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-end">
                          <span>Points Against</span>
                          <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                        </div>
                      </SortableHeader>
                      <SortableHeader field="differential" className="text-right py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-end">
                          <span>Differential</span>
                          <span className="text-xs text-text-tertiary font-normal">Regular Season</span>
                        </div>
                      </SortableHeader>
                      <th className="text-center py-3 px-4 text-text-secondary font-medium text-sm">
                        <div className="flex flex-col items-center">
                          <span>Draft</span>
                          <span className="text-xs text-text-tertiary font-normal">Grade</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedStandings(standings.standings).map((team) => {
                      const madePlayoffs = team.rank <= 6;
                      return (
                        <tr
                          key={team.team_id}
                          className="border-b border-fieldline hover:bg-background-tertiary/60 cursor-pointer transition-colors"
                          style={{
                            boxShadow: team.is_champion
                              ? 'inset 3px 0 0 0 #2FBF71'
                              : madePlayoffs
                                ? 'inset 3px 0 0 0 rgba(47,191,113,0.35)'
                                : 'inset 3px 0 0 0 transparent',
                          }}
                          onClick={() => handleTeamClick(team.team_id)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center space-x-2">
                              <span className={`font-mono font-bold ${team.is_champion ? 'text-accent-primary' : 'text-text-primary'}`}>
                                #{team.rank}
                              </span>
                              {!team.is_champion && madePlayoffs && (
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary/60" />
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-text-primary font-medium">{team.team_name}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="font-mono text-text-secondary">#{team.regular_season?.rank || 'N/A'}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="font-mono text-text-primary">
                              {team.regular_season?.wins || 0}-{team.regular_season?.losses || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-mono text-text-primary">
                              {Number(team.regular_season?.points_for || 0).toFixed(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className="font-mono text-text-primary">
                              {Number(team.regular_season?.points_against || 0).toFixed(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <span className={`font-mono font-medium ${
                              (team.regular_season?.point_differential || 0) >= 0 ? 'text-performance-elite' : 'text-performance-bust'
                            }`}>
                              {(team.regular_season?.point_differential || 0) >= 0 ? '+' : ''}
                              {Number(team.regular_season?.point_differential || 0).toFixed(1)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <GradeStamp grade={draftGradeByTeam.get(team.team_id)} size="xs" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile list — Rank · Team · W-L · PF, tap to expand PA/differential */}
              <div className="md:hidden">
                <div className="flex items-center gap-3 pb-2 mb-1 border-b border-fieldline text-xs uppercase tracking-wide text-text-secondary">
                  <span className="w-8">Rank</span>
                  <span className="flex-1">Team</span>
                  <span className="w-12 text-right">W-L</span>
                  <span className="w-16 text-right">PF</span>
                  <span className="w-4" />
                </div>
                {getSortedStandings(standings.standings).map((team) => {
                  const madePlayoffs = team.rank <= 6;
                  const isOpen = expandedTeam === team.team_id;
                  return (
                    <div
                      key={team.team_id}
                      className="border-b border-fieldline"
                      style={{
                        boxShadow: team.is_champion
                          ? 'inset 3px 0 0 0 #2FBF71'
                          : madePlayoffs
                            ? 'inset 3px 0 0 0 rgba(47,191,113,0.35)'
                            : 'inset 3px 0 0 0 transparent',
                      }}
                    >
                      <button
                        className="w-full flex items-center gap-3 py-3 text-left"
                        onClick={() => setExpandedTeam(isOpen ? null : team.team_id)}
                      >
                        <span className={`w-8 font-mono font-bold ${team.is_champion ? 'text-accent-primary' : 'text-text-primary'}`}>
                          #{team.rank}
                        </span>
                        <span className="flex-1 min-w-0 truncate text-text-primary font-medium">{team.team_name}</span>
                        <span className="w-12 text-right font-mono text-text-primary">
                          {team.regular_season?.wins || 0}-{team.regular_season?.losses || 0}
                        </span>
                        <span className="w-16 text-right font-mono text-text-primary">
                          {Number(team.regular_season?.points_for || 0).toFixed(1)}
                        </span>
                        {isOpen
                          ? <ChevronUp className="w-4 h-4 text-text-secondary flex-shrink-0" />
                          : <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0" />}
                      </button>
                      {isOpen && (
                        <div className="pb-3 pl-11 pr-1 grid grid-cols-2 gap-y-2 text-sm">
                          <span className="text-text-secondary">Points Against</span>
                          <span className="text-right font-mono text-text-primary">
                            {Number(team.regular_season?.points_against || 0).toFixed(1)}
                          </span>
                          <span className="text-text-secondary">Differential</span>
                          <span className={`text-right font-mono font-medium ${
                            (team.regular_season?.point_differential || 0) >= 0 ? 'text-performance-elite' : 'text-performance-bust'
                          }`}>
                            {(team.regular_season?.point_differential || 0) >= 0 ? '+' : ''}
                            {Number(team.regular_season?.point_differential || 0).toFixed(1)}
                          </span>
                          <span className="text-text-secondary">Reg. Season Rank</span>
                          <span className="text-right font-mono text-text-primary">#{team.regular_season?.rank || 'N/A'}</span>
                          <span className="text-text-secondary">Draft Grade</span>
                          <span className="text-right">
                            <GradeStamp grade={draftGradeByTeam.get(team.team_id)} size="xs" />
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};

export default LeagueOverview;
